import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { DayNote, Item, Group, UpdateItemRequest } from '@daynote/shared-types';
import { getDataSource } from '../data';
import { getLunarInfo, getHoliday } from '@daynote/utils';
import { useGroupsStore } from '../store/groups';
import { CloseIcon, CheckIcon, DragIcon } from './ui/Icons';
import { PRESET_COLORS } from '../lib/colors';

/** 解析 "[简写] 正文" 形式，命中已有分组则返回 { groupId, content } */
function parseAbbrPrefix(
  raw: string,
  findByAbbr: (abbr: string) => Group | undefined,
): { groupId: string | null; content: string } {
  const match = raw.match(/^\s*\[([^\]]+)\]\s*(.*)$/s);
  if (match) {
    const group = findByAbbr(match[1]!.trim());
    if (group) return { groupId: group.id, content: match[2]!.trim() };
  }
  return { groupId: null, content: raw.trim() };
}

/** 分组选择 + 时长输入，新建与编辑复用 */
function GroupHoursControls({
  groups,
  groupId,
  hours,
  onGroupChange,
  onHoursChange,
}: {
  groups: Group[];
  groupId: string | null;
  hours: number | null;
  onGroupChange: (groupId: string | null) => void;
  onHoursChange: (hours: number | null) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <select
        value={groupId ?? ''}
        onChange={(e) => onGroupChange(e.target.value || null)}
        className="bg-white/5 border border-[var(--grid-line)] rounded px-2 py-1 text-xs text-daynote-text focus:outline-none focus:border-daynote-accent cursor-pointer"
      >
        <option value="">{t('reports.ungrouped')}</option>
        {groups.map((g) => (
          <option key={g.id} value={g.id}>
            [{g.abbr}] {g.name}
          </option>
        ))}
      </select>
      <div className="flex items-center gap-1">
        <input
          type="number"
          min={0}
          max={24}
          step={0.5}
          value={hours ?? ''}
          onChange={(e) => {
            const v = e.target.value;
            onHoursChange(v === '' ? null : Math.max(0, Math.min(24, Number(v))));
          }}
          placeholder={t('dayDetail.hoursPlaceholder')}
          className="w-16 bg-white/5 border border-[var(--grid-line)] rounded px-2 py-1 text-xs text-daynote-text focus:outline-none focus:border-daynote-accent"
        />
        <span className="text-xs text-daynote-muted">{t('dayDetail.hoursUnit')}</span>
      </div>
    </div>
  );
}

interface Props {
  date: string;
  isMobile?: boolean;
  onClose: () => void;
  onChange: (dayNote: DayNote) => void;
}

const MONTH_EN = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function SortableItemRow({
  item,
  index,
  isMobile,
  groups,
  onToggle,
  onDelete,
  onEdit,
  onColorChange,
  onNoteEdit,
  onGroupChange,
  onHoursChange,
}: {
  item: Item;
  index: number;
  isMobile: boolean;
  groups: Group[];
  onToggle: () => void;
  onDelete: () => void;
  onEdit: (content: string) => void;
  onColorChange: (color: string | null) => void;
  onNoteEdit: (note: string | null) => void;
  onGroupChange: (groupId: string | null) => void;
  onHoursChange: (hours: number | null) => void;
}) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  });
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.content);
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteDraft, setNoteDraft] = useState(item.note ?? '');
  const [colorOpen, setColorOpen] = useState(false);
  const [swipeX, setSwipeX] = useState(0);
  const touchStart = useRef<number | null>(null);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // 条目未单独设色时继承分组色
  const effectiveColor = item.color ?? item.group?.color ?? null;

  const saveContent = () => {
    if (draft.trim() && draft !== item.content) onEdit(draft.trim());
    else setDraft(item.content);
    setEditing(false);
  };

  const saveNote = () => {
    onNoteEdit(noteDraft.trim() || null);
    setNoteOpen(false);
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    setColorOpen(true);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStart.current = e.touches[0]?.clientX ?? null;
    longPressTimer.current = setTimeout(() => setColorOpen(true), 500);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
    if (touchStart.current === null || !isMobile) return;
    const dx = (e.touches[0]?.clientX ?? 0) - touchStart.current;
    if (dx < 0) setSwipeX(Math.max(dx, -80));
  };

  const handleTouchEnd = () => {
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
    if (swipeX < -50) onDelete();
    setSwipeX(0);
    touchStart.current = null;
  };

  return (
    <div ref={setNodeRef} style={style} className="relative mb-1.5">
      {isMobile && swipeX < -20 && (
        <div className="absolute inset-y-0 right-0 flex items-center px-4 bg-daynote-work rounded-lg">
          <span className="text-xs text-white">{t('dayDetail.delete')}</span>
        </div>
      )}
      <div
        className={`group flex items-start gap-3 p-3 rounded-lg border border-transparent hover:bg-white/5 hover:border-[var(--grid-line)] relative transition-transform ${
          item.isDone ? 'opacity-80' : ''
        }`}
        style={{ transform: `translateX(${swipeX}px)` }}
        onContextMenu={handleContextMenu}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {effectiveColor && (
          <div
            className="absolute left-0 top-0 bottom-0 w-1 rounded-l-lg"
            style={{ background: effectiveColor }}
          />
        )}

        {!isMobile && (
          <button
            type="button"
            className="mt-1 text-daynote-muted/40 hover:text-daynote-muted cursor-grab opacity-0 group-hover:opacity-100 transition-opacity bg-transparent border-none"
            {...attributes}
            {...listeners}
          >
            <DragIcon />
          </button>
        )}

        <button
          type="button"
          onClick={onToggle}
          className={`w-5 h-5 mt-0.5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
            item.isDone
              ? 'bg-daynote-accent border-daynote-accent'
              : 'border-daynote-muted hover:border-daynote-accent'
          }`}
        >
          {item.isDone && <CheckIcon className="text-[#2a2520]" />}
        </button>

        <div className="flex-1 min-w-0">
          {editing ? (
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={saveContent}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  saveContent();
                }
              }}
              autoFocus
              rows={2}
              className="w-full bg-transparent text-sm focus:outline-none resize-y"
            />
          ) : (
            <div
              className={`text-sm leading-relaxed flex items-start gap-2 cursor-text ${item.isDone ? 'line-through text-white/45' : ''}`}
              onClick={() => setEditing(true)}
            >
              {!effectiveColor && (
                <span className="font-display italic text-daynote-muted text-[13px] flex-shrink-0">
                  {index + 1}.
                </span>
              )}
              <span className="break-all">
                {item.group && (
                  <span className="text-daynote-accent font-medium mr-1">[{item.group.abbr}]</span>
                )}
                {item.content}
                {item.hours != null && (
                  <span className="text-daynote-muted text-xs ml-1.5 whitespace-nowrap">
                    · {item.hours}h
                  </span>
                )}
              </span>
            </div>
          )}

          {item.note && !noteOpen && (
            <div
              className="text-xs text-daynote-muted mt-1.5 pl-2 border-l-2 border-white/10 cursor-pointer"
              onClick={() => {
                setNoteDraft(item.note ?? '');
                setNoteOpen(true);
              }}
            >
              {item.note}
            </div>
          )}

          {noteOpen && (
            <div className="mt-2">
              <textarea
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value)}
                placeholder={t('dayDetail.notePlaceholder')}
                rows={2}
                className="w-full bg-white/5 border border-[var(--grid-line)] rounded p-2 text-xs focus:outline-none focus:border-daynote-accent resize-y"
              />
              <div className="flex gap-2 mt-1">
                <button
                  type="button"
                  onClick={saveNote}
                  className="text-xs text-daynote-accent bg-transparent border-none cursor-pointer"
                >
                  {t('dayDetail.save')}
                </button>
                <button
                  type="button"
                  onClick={() => setNoteOpen(false)}
                  className="text-xs text-daynote-muted bg-transparent border-none cursor-pointer"
                >
                  {t('dayDetail.cancel')}
                </button>
              </div>
            </div>
          )}

          {!item.note && !noteOpen && (
            <button
              type="button"
              onClick={() => setNoteOpen(true)}
              className="text-[11px] text-daynote-muted/60 mt-1 bg-transparent border-none cursor-pointer hover:text-daynote-muted"
            >
              + {t('dayDetail.notePlaceholder')}
            </button>
          )}

          <div className="mt-2 opacity-60 group-hover:opacity-100 transition-opacity">
            <GroupHoursControls
              groups={groups}
              groupId={item.groupId}
              hours={item.hours}
              onGroupChange={onGroupChange}
              onHoursChange={onHoursChange}
            />
          </div>
        </div>

        {!isMobile && (
          <button
            type="button"
            onClick={onDelete}
            className="opacity-0 group-hover:opacity-100 text-xs text-daynote-muted hover:text-daynote-work transition-opacity bg-transparent border-none cursor-pointer"
          >
            {t('dayDetail.delete')}
          </button>
        )}
      </div>

      {colorOpen && (
        <div className="absolute left-8 right-8 top-full mt-1 z-20 bg-black/90 border border-[var(--grid-line)] rounded-lg p-3 shadow-xl">
          <div className="flex gap-2 flex-wrap items-center">
            {PRESET_COLORS.map((c, i) => (
              <button
                key={i}
                type="button"
                onClick={() => {
                  onColorChange(c);
                  setColorOpen(false);
                }}
                className={`w-6 h-6 rounded-full ${item.color === c ? 'ring-2 ring-white' : ''}`}
                style={{
                  background: c ?? 'transparent',
                  border: c ? 'none' : '1.5px dashed #b8aa95',
                }}
              />
            ))}
            <label className="relative cursor-pointer" title={t('dayDetail.customColor')}>
              <input
                type="color"
                className="sr-only"
                onChange={(e) => {
                  onColorChange(e.target.value);
                  setColorOpen(false);
                }}
              />
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full border border-dashed border-daynote-accent text-[10px] text-daynote-accent">
                +
              </span>
            </label>
          </div>
          <button
            type="button"
            onClick={() => setColorOpen(false)}
            className="mt-2 text-xs text-daynote-muted bg-transparent border-none cursor-pointer"
          >
            {t('dayDetail.close')}
          </button>
        </div>
      )}
    </div>
  );
}

export function DayDetailPanel({ date, isMobile, onClose, onChange }: Props) {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language.startsWith('en');
  const groups = useGroupsStore((s) => s.groups);
  const groupsLoaded = useGroupsStore((s) => s.loaded);
  const loadGroups = useGroupsStore((s) => s.load);
  const findByAbbr = useGroupsStore((s) => s.findByAbbr);
  const [dayNote, setDayNote] = useState<DayNote | null>(null);
  const [editing, setEditing] = useState<'new' | null>('new');
  const [draft, setDraft] = useState('');
  const [draftColor, setDraftColor] = useState<string | null>(null);
  const [draftNote, setDraftNote] = useState('');
  const [draftGroupId, setDraftGroupId] = useState<string | null>(null);
  const [draftHours, setDraftHours] = useState<number | null>(null);

  useEffect(() => {
    if (!groupsLoaded) loadGroups();
  }, [groupsLoaded, loadGroups]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => {
    getDataSource()
      .getByDate(date)
      .then(({ dayNote: dn }) => {
        setDayNote(dn);
        setEditing(dn?.items.length ? null : 'new');
      });
  }, [date]);

  const lunar = getLunarInfo(date);
  const holiday = getHoliday(date);
  const [, m, d] = date.split('-').map(Number);
  const monthEn = MONTH_EN[(m ?? 1) - 1];
  const monthLocalized = t(`months.${m}`); // e.g. "三月" or "March"

  const refreshDayNote = async () => {
    const { dayNote: dn } = await getDataSource().getByDate(date);
    if (dn) {
      setDayNote(dn);
      onChange(dn);
    }
  };

  const addItem = async () => {
    if (!draft.trim()) return;
    // 支持 "[简写] 正文" 快捷指定分组；显式选择的分组优先
    const parsed = parseAbbrPrefix(draft, findByAbbr);
    const groupId = draftGroupId ?? parsed.groupId;
    await getDataSource().createItem(date, {
      content: parsed.content || draft.trim(),
      color: draftColor,
      note: draftNote.trim() || undefined,
      groupId,
      hours: draftHours,
    });
    await refreshDayNote();
    setDraft('');
    setDraftColor(null);
    setDraftNote('');
    setDraftGroupId(null);
    setDraftHours(null);
    setEditing(null);
  };

  const updateItem = async (id: string, patch: UpdateItemRequest) => {
    const updated = await getDataSource().updateItem(id, patch);
    setDayNote((dn) => {
      if (!dn) return dn;
      const items = dn.items.map((it) => (it.id === id ? updated : it));
      const next = { ...dn, items };
      onChange(next);
      return next;
    });
  };

  const deleteItem = async (id: string) => {
    await getDataSource().deleteItem(id);
    setDayNote((dn) => {
      if (!dn) return dn;
      const items = dn.items.filter((it) => it.id !== id);
      const next = { ...dn, items };
      onChange(next);
      return next;
    });
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !dayNote) return;
    const oldIndex = dayNote.items.findIndex((it) => it.id === active.id);
    const newIndex = dayNote.items.findIndex((it) => it.id === over.id);
    const items = arrayMove(dayNote.items, oldIndex, newIndex);
    const next = { ...dayNote, items };
    setDayNote(next);
    onChange(next);
    await getDataSource().updateOrder(
      dayNote.id,
      items.map((it) => it.id),
    );
  };

  const doneCount = dayNote?.items.filter((it) => it.isDone).length ?? 0;
  const totalCount = dayNote?.items.length ?? 0;

  const panelClass = isMobile
    ? 'fixed inset-0 z-50 flex flex-col bg-[rgba(20,14,10,0.95)] backdrop-blur-2xl'
    : 'fixed inset-y-0 right-0 w-[540px] z-50 flex flex-col bg-[rgba(20,14,10,0.92)] backdrop-blur-[40px] border-l border-[var(--grid-line)] shadow-[-20px_0_60px_rgba(0,0,0,0.5)]';

  return (
    <>
      {!isMobile && (
        <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} aria-hidden />
      )}
      <div className={panelClass}>
        <div className="p-7 md:p-8 border-b border-[var(--grid-line)] flex-shrink-0">
          <div className="flex justify-between items-start mb-4">
            <div className="flex items-baseline gap-3.5">
              <span className="font-display text-[56px] font-medium leading-none tracking-tight">{d}</span>
              <div>
                <div className="font-display text-[17px] text-daynote-accent tracking-wide">
                  {isEn ? `${monthLocalized} ${d}` : `${monthEn} / ${m} ${t('dayDetail.monthUnit')}`}
                </div>
                {!isEn && (
                  <div className="text-[13px] text-daynote-muted tracking-wide">
                    {t('dayDetail.lunarLabel')} {lunar.lunarMonth}
                  </div>
                )}
                {!isEn && holiday && (
                  <div className="text-[13px] text-daynote-warm mt-0.5">{holiday.name}</div>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="w-9 h-9 rounded-full border border-[var(--grid-line)] text-daynote-muted hover:text-daynote-text flex items-center justify-center bg-transparent cursor-pointer"
            >
              <CloseIcon />
            </button>
          </div>

          {totalCount > 0 && (
            <div className="flex gap-4 px-3.5 py-3 bg-white/[0.03] rounded-lg border border-[var(--grid-line)]">
              <div>
                <div className="font-display text-[22px] font-medium">{totalCount}</div>
                <div className="text-[11px] text-daynote-text/55 tracking-wide">{t('reports.totalItems')}</div>
              </div>
              <div className="w-px bg-[var(--grid-line)]" />
              <div>
                <div className="font-display text-[22px] font-medium">{doneCount}</div>
                <div className="text-[11px] text-daynote-text/55 tracking-wide">{t('reports.doneItems')}</div>
              </div>
              <div className="w-px bg-[var(--grid-line)]" />
              <div>
                <div className="font-display text-[22px] font-medium">
                  {totalCount > 0 ? Math.round((doneCount / totalCount) * 100) : 0}%
                </div>
                <div className="text-[11px] text-daynote-text/55 tracking-wide">{t('dayDetail.completion')}</div>
              </div>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-7 md:px-8 py-5 pb-24">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext
              items={dayNote?.items.map((it) => it.id) ?? []}
              strategy={verticalListSortingStrategy}
            >
              {dayNote?.items.map((item, index) => (
                <SortableItemRow
                  key={item.id}
                  item={item}
                  index={index}
                  isMobile={!!isMobile}
                  groups={groups}
                  onToggle={() => updateItem(item.id, { isDone: !item.isDone })}
                  onDelete={() => deleteItem(item.id)}
                  onEdit={(content) => updateItem(item.id, { content })}
                  onColorChange={(color) => updateItem(item.id, { color })}
                  onNoteEdit={(note) => updateItem(item.id, { note })}
                  onGroupChange={(groupId) => updateItem(item.id, { groupId })}
                  onHoursChange={(hours) => updateItem(item.id, { hours })}
                />
              ))}
            </SortableContext>
          </DndContext>

          {editing === 'new' ? (
            <div className="bg-daynote-accent/5 border border-daynote-accent/25 p-4 rounded-lg mt-2">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                autoFocus
                rows={2}
                placeholder={t('dayDetail.placeholder')}
                className="w-full bg-transparent text-sm focus:outline-none resize-y"
              />
              <textarea
                value={draftNote}
                onChange={(e) => setDraftNote(e.target.value)}
                rows={1}
                placeholder={t('dayDetail.notePlaceholder')}
                className="w-full bg-transparent text-xs text-daynote-muted mt-2 focus:outline-none resize-y"
              />
              <div className="mt-2">
                <GroupHoursControls
                  groups={groups}
                  groupId={draftGroupId}
                  hours={draftHours}
                  onGroupChange={setDraftGroupId}
                  onHoursChange={setDraftHours}
                />
              </div>
              <div className="flex justify-between mt-3 pt-3 border-t border-[var(--grid-line)]">
                <div className="flex gap-1.5 items-center flex-wrap">
                  {PRESET_COLORS.map((c, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setDraftColor(c)}
                      className={`w-5 h-5 rounded-full ${draftColor === c ? 'ring-2 ring-white' : ''}`}
                      style={{
                        background: c ?? 'transparent',
                        border: c ? 'none' : '1.5px dashed #b8aa95',
                      }}
                    />
                  ))}
                  <label className="cursor-pointer" title={t('dayDetail.customColor')}>
                    <input
                      type="color"
                      className="sr-only"
                      onChange={(e) => {
                        setDraftColor(e.target.value);
                      }}
                    />
                    <span className="inline-flex w-5 h-5 items-center justify-center rounded-full border border-dashed border-daynote-accent text-[8px] text-daynote-accent">
                      +
                    </span>
                  </label>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setEditing(null);
                      setDraft('');
                    }}
                    className="px-3 py-1 text-xs text-daynote-muted border border-[var(--grid-line)] rounded bg-transparent cursor-pointer"
                  >
                    {t('dayDetail.cancel')}
                  </button>
                  <button
                    type="button"
                    onClick={addItem}
                    className="px-4 py-1 text-xs bg-daynote-accent text-[#2a2520] rounded font-medium border-none cursor-pointer"
                  >
                    {t('dayDetail.save')}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setEditing('new')}
              className="w-full flex items-center gap-3 p-4 border-2 border-dashed border-[var(--grid-line)] rounded-lg text-daynote-muted hover:border-daynote-accent hover:text-daynote-accent transition-colors mt-3 bg-transparent cursor-pointer"
            >
              <span className="font-display text-lg">＋</span>
              <span className="text-sm">{t('dayDetail.addItem')}</span>
            </button>
          )}
        </div>
      </div>
    </>
  );
}
